import {
    CountMetric,
    LatencyMetric,
    Exception,
    InvalidInputException, AlternativeBotException, InvalidSpeechException, InvalidSpeechInputException,
    PauseIntentException, RepeatIntentException, SpeechRecognitionException, VoiceDependencyFailureException,
    StopIntentException, UnexpectedIntentException, UnrecognizedSpeechException, UnsupportedBrowserException,
    UUIDUtils, StringUtils,
} from '@amzn/ihm-associate-tools-js-lib'
import * as VoiceUtilsService from '@utils/VoiceUtilsService'
import * as AudioProcessingService from '@services/AudioProcessingService'
import * as AudioRecordingService from '@services/AudioRecordingService'
import * as VoiceAudioCuesService from '@services/VoiceAudioCuesService'
import * as AwsClientProviderService from '@services/AwsClientProviderService'
import * as UserRoleService from '@services/UserRoleService'
import _ from 'lodash'
import { generateDeferredPromise } from '@utils/DeferredPomiseUtil'
import { PostContentCommand } from '@aws-sdk/client-lex-runtime-service'

export const SpeechRecognitionServiceConfig = Object.freeze({
    // Required by Lex: https://tiny.amazon.com/efx4z1h0/docsawsamazlexlatedgAPI_html
    TARGET_AUDIO_SAMPLE_RATE: 16000,
    DEFAULT_RECORD_TIME_MILLIS: 2000,
    ALTERNATIVE_BOTS_MAX_LENGTH: 3,
})

const SpeechRecognitionUnexpectedIntentName = Object.freeze({
    PAUSE_INTENT: 'Peppermint_PauseIntent',
    REPEAT_INTENT: 'Peppermint_RepeatIntent',
    STOP_INTENT: 'Peppermint_StopIntent',
})

export const SpeechRecognitionBotConfiguration = Object.freeze({
    GET_POSITIVE_INTEGER: Object.freeze({
        botName: 'Peppermint_GetNumberBot',
        recordTimeInMillis: 2000,
        expectedIntentName: 'Peppermint_GetNumberIntent',
        expectedSlotName: 'Peppermint_GetNumberSlot',
    }),
    GET_NUMBER: Object.freeze({
        botName: 'Peppermint_GetNumberBot',
        recordTimeInMillis: 2000,
        expectedIntentName: 'Peppermint_GetNumberIntent',
        expectedSlotName: 'Peppermint_GetNumberSlot',
    }),
    GET_ALPHANUMERIC: Object.freeze({
        botName: 'Peppermint_GetAlphanumericBot',
        recordTimeInMillis: 2000,
        expectedIntentName: 'Peppermint_GetAlphanumericIntent',
        expectedSlotName: 'Peppermint_GetAlphanumericSlot',
    }),
    GET_CONFIRMATION: Object.freeze({
        botName: 'Peppermint_GetConfirmationBot',
        recordTimeInMillis: 2000,
        expectedIntentName: 'Peppermint_GetConfirmationIntent',
        expectedSlotName: 'Peppermint_GetConfirmationSlot',
    }),
    GET_DATE: Object.freeze({
        botName: 'Peppermint_GetDateBot',
        recordTimeInMillis: 2000,
        expectedIntentName: 'Peppermint_GetDateIntent',
        expectedSlotName: 'Peppermint_GetDateSlot',
    }),
})

const programName = 'IhmFlyingDutchman'

export class SpeechRecognitionService {

    private static dateValidationRegex = /^[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/
    private static lexConfigTemplate = Object.freeze({
        botAlias: 'latest',
        contentType: 'audio/x-l16; sample-rate=16000; channel-count=1', /* required */
        accept: 'text/plain; charset=utf-8',

        // will be filled in at runtime
        botName: null,
        inputStream: null,
        userId: null,
    })

    // Public functions

    /**
     * Creates the Lex client along with its credentials and attempts to warm up the audio recorder.
     *
     * @return {Promise} Resolves when warmup is done successfully. Rejects otherwise. Note that the warmup method
     *  is there only for latency purposes so some failures may be transient.
     */
    public static warmup () {

        const warmupFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'speechRecognitionService.warmup')
        const warmupLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'speechRecognitionService.warmup')
        warmupLatencyMetrics.start()

        return VoiceUtilsService.verifyVoiceIsEnabled()
            .then(() => {
                return AudioRecordingService.warmup()
                    .catch((error) => {
                        if (error instanceof UnsupportedBrowserException) {
                            return Promise.reject(error)
                        }
                    })
            })
            .then(() => {
                return Promise.all([
                    SpeechRecognitionService.getClient(),
                    VoiceAudioCuesService.warmup(),
                ])
            })
            .then(() => {
                // We swallow the client here because we just want the client generation to be cached. Also, we don't
                // want to expose the client to the caller (this is a public static function).
                // A call to speak should get the same client we would get here.
                warmupFaultMetrics.count(0)
            })
            .catch((error) => {
                console.error('speechRecognitionService warmup() failed with error ', error)
                warmupFaultMetrics.count(1)

                if (error instanceof Exception) {
                    return Promise.reject(error)
                }
                return Promise.reject(new SpeechRecognitionException({
                    message: 'Unknown error occurred while initializing the SpeechRecognitionService',
                }))
            })
            .finally(() => {
                warmupLatencyMetrics.stop()
            })
    }

    /**
     * Calls a custom Lex bot decided by the `dataType` key.
     *
     * @param {object} customConfig - Configuration for the speechRecognition engine. Must include the 'dataType' key, and,
     *  optionally, the recordTimeInMillis key.
     *  Example:
     *      {
     *          // Note that you don't need to prepend the 'Custom_' or append the 'Bot' at the end
     *          dataType: 'PrimeNow_GetPickZone',
     *          recordTimeInMillis: 1000, // Defaults to SpeechRecognitionServiceConfig.DEFAULT_RECORD_TIME_MILLIS
     *          alternativeBots: ['PrimeNow_GetProblemSolve'] // Defaults to empty array
     *      }
     * @return {Promise} Resolves with a {ResponseObject} (see this service's documentation) and rejects with appropriate exceptions (see this service's
     *  docs).
     */
    public static getCustomDataType (customConfig) {
        return SpeechRecognitionService.validateArgsAndGenerateCustomConfig(customConfig)
            .then((config) => {
                return SpeechRecognitionService.getSpeechRecognitionRequestPromise(config, config, SpeechRecognitionService.processDefault)
            })
    }

    /**
     * Records speech and extracts the confirmation from it. The data type returned is always string with:
     *  "Yes" - For yes
     *  "No" - For no
     *
     * @param {object} config - Configuration object (see configs in this service's doc).
     * @return {Promise} Resolves with a {ResponseObject} with the extracted confirmation. For rejections see this service's docs.
     */
    public static getConfirmation (config) {
        return SpeechRecognitionService.getSpeechRecognitionRequestPromise(config, SpeechRecognitionBotConfiguration.GET_CONFIRMATION, SpeechRecognitionService.processDefault)
    }

    /**
     * Records speech and extracts the alphanumeric input from it. The data returned is always string (for example "AB103")
     *
     * @param {object} config - Configuration object (see configs in this service's doc).
     * @return {Promise} Resolves with a {ResponseObject} with the extracted alphanumeric input. For rejections see this service's docs.
     */
    public static getAlphanumeric (config) {
        return SpeechRecognitionService.getSpeechRecognitionRequestPromise(config, SpeechRecognitionBotConfiguration.GET_ALPHANUMERIC, SpeechRecognitionService.processDefault)
    }

    /**
     * Records speech and extracts the date from it. The data type returned is always string in the "yyyy-mm-dd" format.
     * More information here: https://developer.amazon.com/docs/custom-skills/slot-type-reference.html#date
     *
     * @param {object} config - Configuration object (see configs in this service's doc).
     * @return {Promise} Resolves with a {ResponseObject} with the extracted date. For rejections see this service's docs.
     */
    public static getDate (config) {
        return SpeechRecognitionService.getSpeechRecognitionRequestPromise(config, SpeechRecognitionBotConfiguration.GET_DATE, SpeechRecognitionService.processDate)
    }

    /**
     * Records speech and extracts the numeric input from it. The data returned is always Number and can be either integer or
     * float (for example 10 or 10.5).
     *
     * @param {object} config - Configuration object (see configs in this service's doc).
     * @return {Promise} Resolves with a {ResponseObject} with the extracted numeric input. For rejections see this service's docs.
     */
    public static getNumber (config) {
        return SpeechRecognitionService.getSpeechRecognitionRequestPromise(config, SpeechRecognitionBotConfiguration.GET_NUMBER, SpeechRecognitionService.processNumber)
    }

    /**
     * Records speech and extracts the positive integer input from it. The data returned is always Number and is always an
     * integer (for example 10).
     *
     * @param {object} config - Configuration object (see configs in this service's doc).
     * @return {Promise} Resolves with a {ResponseObject} with the extracted numeric input. For rejections see this service's docs.
     */
    public static getPositiveInteger (config) {
        return SpeechRecognitionService.getSpeechRecognitionRequestPromise(config, SpeechRecognitionBotConfiguration.GET_POSITIVE_INTEGER, SpeechRecognitionService.processPositiveNumber)
    }

    // Private functions

    /**
     * Gets the LexRuntime client. We don't cache this promise because the client is already cached and the underlying implementation could
     * return a new client if credentials expire.
     *
     * @return {Promise} Resolves with a LexRuntime client.
     */
    private static getClient () {
        return AwsClientProviderService.getAwsClient(AwsClientProviderService.AwsClient.LEX_RUNTIME, UserRoleService.UserRole.VOICE_USER_ROLE, '')
            .catch((error) => {
                console.error('Error creating the client', error)
                return Promise.reject(new VoiceDependencyFailureException({
                    message: 'There was a problem creating the client',
                }))
            })
    }

    /**
     * Verify voice is enabled and record audio.
     *
     * @param {object} config - must have the same structure as defined in SpeechRecognitionBotConfiguration
     */
    private static getAudioRecording (config) {
        const botName = config.botName
        const faultMetrics = new CountMetric(undefined, 'Fault', programName, 'speechRecognitionService.' + botName)

        return VoiceUtilsService.verifyVoiceIsEnabled()
            .then(() => {
                return SpeechRecognitionService.recordAndProcessAudio(config)
            })
            .catch((error) => {
                return SpeechRecognitionService.rejectWithCorrectExceptionAndEmitFaultMetric(error, faultMetrics)
            })
    }

    /**
     * Call each of the necessary lex bots.
     * If all bots fail for any reason, the exception to the target bot (bot at first index in array) will be returned
     * If one or more bots succeed, the {ResponseObject} of the bot at the lowest index will be returned
     * If a bot other than the target bot succeeds, and the target bot rejects with {UnrecognizedSpeechException},
     *  an {AlternativeBotException} will be thrown containing a message and {ResponseObject}
     * If a bot other than the target bot succeeds, and the target bot rejects with exception other than {UnrecognizedSpeechException},
     *  the exception to the target bot will be returned
     *
     * @param {audioBuffer} audioBuffer - The audio buffer to stream to Lex.
     * @param {array} botConfigurations - List of lex bot configuration objects of the bots to call
     *
     * @return {Promise} - {ResponseObject} when it resolves, see this service's docs for rejections.
     *  Example {ResponseObject} when a bot succeeds:
     *      {
     *          data: 10,
     *          botName: 'Peppermint_GetNumberBot'
     *      }
     *
     *  Example {AlternativeBotException} when a bot other than the target bot succeeds:
     *      {
     *          message: 'An alternative bot recognized speech',
     *          extraData: {
     *              data: 10,
     *              botName: 'Peppermint_GetNumberBot'
     *          }
     *      }
     */
    private static getBotResponses (audioBuffer, botConfigurations) {
        const promiseArray = botConfigurations.map((botConfiguration) => {
            return SpeechRecognitionService.getSpeechRecognitionWorkflowPromise(botConfiguration, audioBuffer)
        })

        return SpeechRecognitionService.allSettled(promiseArray)
            .then((settledPromises) => {
                console.debug('SpeechRecognitionService: All promises settled', settledPromises)
                const firstFulfilledIndex = _.findIndex(settledPromises, (settledPromise) => {
                    return settledPromise.status === 'fulfilled'
                }, 0)

                if (firstFulfilledIndex === -1) {
                    console.error('No bot recognized speech')
                    return Promise.reject(settledPromises[0].reason)
                }

                if (firstFulfilledIndex === 0) {
                    console.info('Bot recognized speech')
                    return settledPromises[0].value
                }

                if (settledPromises[0].reason instanceof UnrecognizedSpeechException) {
                    console.info('Alternative bot recognized speech')
                    const successCountMetrics = new CountMetric(undefined, 'AlternativeBot', programName, 'speechRecognitionService.getBotResponses.' + settledPromises[firstFulfilledIndex].value.botName)
                    successCountMetrics.count(1)
                    return Promise.reject(new AlternativeBotException({
                        message: 'An alternative bot recognized speech',
                        extraData: {
                            data: settledPromises[firstFulfilledIndex].value.data,
                            botName: settledPromises[firstFulfilledIndex].value.botName,
                        },
                    }))
                }

                return Promise.reject(settledPromises[0].reason)
            })
    }

    /**
     * Makes calls to get audio recording and lex bot responses.
     *
     * @param {object} config - config object passed by the caller
     * @param {object} targetBotConfig - must have the same structure as defined in SpeechRecognitionBotConfiguration
     * @param {function} postProcessCallback - Callback function that will extract the slot from the Lex response and process it. This
     *  function must return a promise that resolves to a {ResponseObject}.
     */
    private static getSpeechRecognitionRequestPromise (config, targetBotConfig, postProcessCallback) {
        return SpeechRecognitionService.validateAlternativeBotsAndGenerateCustomConfig(config)
            .then((alternativeBotConfigurations) => {
                console.info('Alternative bots validated.')
                return SpeechRecognitionService.getAudioRecording(targetBotConfig)
                    .then((audioBuffer) => {
                        console.log('Audio recorded.')
                        const targetBotConfiguration = Object.assign({}, targetBotConfig, { postProcessCallback })
                        const allBotConfigurations = [targetBotConfiguration].concat(alternativeBotConfigurations)
                        console.debug('Getting bot responses...')
                        return SpeechRecognitionService.getBotResponses(audioBuffer, allBotConfigurations)
                    })
            })
    }

    /**
     * Performs the main workflow/orchestration to send the audio to lex
     * and process the response.
     *
     * @param {object} config - must have the same structure as defined in SpeechRecognitionBotConfiguration.
     * @param {audioBuffer} audioBuffer - The audio buffer to stream to Lex.
     *
     * @return {Promise} - {ResponseObject} when it resolves, see this service's docs for rejections.
     */
    private static getSpeechRecognitionWorkflowPromise (config, audioBuffer) {
        const botName = config.botName
        const faultMetrics = new CountMetric(undefined, 'Fault', programName, 'speechRecognitionService.' + botName)

        console.debug('Initiating SpeechRecognition workflow with config', config)

        return SpeechRecognitionService.recognizeSpeech(botName, audioBuffer)
            .then((response) => {
                return SpeechRecognitionService.rejectIfUnexpectedResponse(config, response)
            })
            .then((validResponse) => {
                return config.postProcessCallback(config, validResponse)
            })
            .then((data) => {
                faultMetrics.count(0)
                return data
            })
            .catch((error) => {
                return SpeechRecognitionService.rejectWithCorrectExceptionAndEmitFaultMetric(error, faultMetrics)
            })
    }

    /**
     * Validates the customConfig and generates a config object in accordance with SpeechRecognitionBotConfiguration.
     * @param {object} customConfig - Configuration for the custom bot (currently only recordTimeInMillis key is accepted).
     * @return {Promise} Resolves if there are no validation issues with a configuration that mimics a SpeechRecognitionBotConfiguration.
     */
    private static validateArgsAndGenerateCustomConfig (customConfig) {
        if (!customConfig) {
            return Promise.reject(new InvalidInputException({
                message: 'customConfig is required',
            }))
        }
        if (!customConfig.dataType) {
            return Promise.reject(new InvalidInputException({
                message: 'customConfig.dataType is required',
            }))
        }

        const customDataType = customConfig.dataType
        customConfig.recordTimeInMillis = customConfig.recordTimeInMillis || SpeechRecognitionServiceConfig.DEFAULT_RECORD_TIME_MILLIS
        customConfig.alternativeBots = customConfig.alternativeBots || []

        return Promise.resolve(SpeechRecognitionService.generateBotConfig(customDataType, customConfig.recordTimeInMillis, customConfig.alternativeBots))
    }

    /**
     * Validates the alternative bots array and generates a config object in accordance with SpeechRecognitionBotConfiguration.
     * @param {object} config - config object passed by caller
     * @return {Promise} Resolves with an array of alternative bot configuration objects
     */
    private static validateAlternativeBotsAndGenerateCustomConfig (config) {
        config = config || {}
        const alternativeBots = config.alternativeBots || []

        if (alternativeBots.length > SpeechRecognitionServiceConfig.ALTERNATIVE_BOTS_MAX_LENGTH) {
            return Promise.reject(new InvalidInputException({
                message: StringUtils.format('alternativeBots array length must be less than or equal to {0}', SpeechRecognitionServiceConfig.ALTERNATIVE_BOTS_MAX_LENGTH),
            }))
        }

        return Promise.resolve(alternativeBots.map((alternativeBot) => {
            return SpeechRecognitionService.generateBotConfig(alternativeBot, SpeechRecognitionServiceConfig.DEFAULT_RECORD_TIME_MILLIS, [])
        }))
    }

    /**
     * Records audio for the specified time and does some processing so that it is correctly formatted for Lex. It also
     * makes sure that the audioRecorder plays the audio cues.
     * @param {object} config - config with the same structure as SpeechRecognitionBotConfiguration.
     * @return {Promise} - Resolves when the audio has been recorded and processed.
     */
    private static recordAndProcessAudio (config) {
        const recordAudioConfig = {
            recordTimeInMillis: config.recordTimeInMillis,
            beforeStartCallback: VoiceAudioCuesService.playListeningStart,
            beforeStopCallback: VoiceAudioCuesService.playListeningEnd,
        }

        return AudioRecordingService.recordAudio(recordAudioConfig)
            .then((buffer) => {
                return AudioProcessingService.resampleAudio(buffer, SpeechRecognitionServiceConfig.TARGET_AUDIO_SAMPLE_RATE)
            })
            .then((downSampledBuffer) => {
                return AudioProcessingService.convertFloat32ToInt16(downSampledBuffer.getChannelData(0)).buffer
            })
    }

    /**
     * Performs the speech recognition  by calling Lex.
     * @param {string} botName - The name of the bot to call.
     * @param {audioBuffer} audioBuffer - The audio buffer to stream to Lex.
     * @return {Promise} Resolves when Lex has responded.
     */
    private static recognizeSpeech (botName, audioBuffer) {
        const config = SpeechRecognitionService.generateLexConfig(botName, audioBuffer)

        return SpeechRecognitionService.getClient()
            .then((lexClient) => {
                return SpeechRecognitionService.sendAudioToBot(lexClient, config)
            })
    }

    /**
     * Sends the audio to the Lex bot.
     * @param {LexRuntime} lexClient - LexRuntime client.
     * @param {object} config - The config for the Lex request.
     * @return {Promise} resolves with a Lex response object and rejects with a VoiceDependencyFailureException.
     */
    private static sendAudioToBot (lexClient, config) {
        const deferredPromise = generateDeferredPromise<any>()
        const lexFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'lex.postContent')
        const lexLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'lex.postContent')
        lexLatencyMetrics.start()

        try {
            new CountMetric(undefined, 'audioSize', programName, 'lex.postContent').count(config.inputStream.byteLength)
            const command = new PostContentCommand(config)
            const response = lexClient.send(command)
            console.debug('Response from Lex', response)
            lexFaultMetrics.count(0)
            // We get rid of this data at the source to prevent inadvertent logging.
            response.inputTranscript = '<--redacted-->'
            deferredPromise.resolve(response)
        } catch (e) {
            console.error('Got error when calling lex', e)
            lexFaultMetrics.count(1)
            deferredPromise.reject(new VoiceDependencyFailureException({
                message: 'Got error while calling lex',
            }))
        } finally {
            lexLatencyMetrics.stop()
        }

        return deferredPromise.promise
    }

    /**
     * Filters the exceptions to decide if it should reject with a different exception and whether it should
     * emit a Fault metric
     *
     * @param {Exception|Error} error - The Exception / error.
     * @param {CountMetric} faultMetric - The metric to use to emit counts.
     */
    private static rejectWithCorrectExceptionAndEmitFaultMetric (error, faultMetric) {
        console.error('Failed to extract data from speech', error)

        if (error instanceof RepeatIntentException || error instanceof StopIntentException || error instanceof PauseIntentException || error instanceof InvalidSpeechException) {
            faultMetric.count(0)
            return Promise.reject(error)
        }

        faultMetric.count(1)
        if (error instanceof Exception) {
            return Promise.reject(error)
        }

        return Promise.reject(new SpeechRecognitionException({
            message: 'Unable to recognize speech',
        }))
    }

    /**
     * Compares the response to see if it matches the expected intent or any of the expected error
     * intents (for example RepeatIntent). Resolves the promise if the intent in the response matches
     * the expected intent. Rejects otherwise with the appropriate exception.
     *
     * @param {object} config - The config used for the bot (the expectedIntentName key is required).
     * @param {object} response - The raw response received from Lex.
     *
     * @return {Promise} - Resolves with the response if the processed intentName matches the expected
     *  intent name. Rejects otherwise:
     *      - UnrecognizedSpeechException - Failure to recognize speech.
     *      - RepeatIntentException -  The user most likely said "Repeat" (the caller needs to handle the repeat).
     *      - PauseIntentException -  The user most likely said "Pause" (the caller needs to handle the pause).
     *      - StopIntentException -  The user most likely said "Stop" (the caller needs to handle the stop).
     *      - UnexpectedIntentException - If we get an intent that we're not expecting at all (could happen if
     *          there's a new intent in the Lex bot but this hasn't been updated to use it).
     */
    private static rejectIfUnexpectedResponse (config, response) {
        const botName = config.botName
        const expectedIntentName = config.expectedIntentName
        const responseIntentName = response.intentName
        const metricOperation = 'speechRecognitionService.' + botName
        const successCountMetric = new CountMetric(undefined, 'success', programName, metricOperation)
        const unexpectedIntentException = new CountMetric(undefined, 'UnexpectedIntent', programName, metricOperation)
        const unrecognizedSpeechCountMetric = new CountMetric(undefined, 'UnrecognizedSpeech', programName, metricOperation)

        if (responseIntentName === expectedIntentName) {
            successCountMetric.count(1)
            unexpectedIntentException.count(0)
            unrecognizedSpeechCountMetric.count(0)
            return Promise.resolve(response)
        }

        successCountMetric.count(0)

        if (responseIntentName === null) {
            unrecognizedSpeechCountMetric.count(1)
            unexpectedIntentException.count(0)
            return Promise.reject(new UnrecognizedSpeechException({
                message: 'Unable to recognize speech',
            }))
        }

        unexpectedIntentException.count(1)
        new CountMetric(undefined, responseIntentName, programName, metricOperation).count(1)

        if (responseIntentName === SpeechRecognitionUnexpectedIntentName.REPEAT_INTENT) {
            unrecognizedSpeechCountMetric.count(0)
            return Promise.reject(new RepeatIntentException({
                message: 'Got repeat intent',
            }))
        }

        if (responseIntentName === SpeechRecognitionUnexpectedIntentName.PAUSE_INTENT) {
            unrecognizedSpeechCountMetric.count(0)
            return Promise.reject(new PauseIntentException({
                message: 'Got pause intent',
            }))
        }

        if (responseIntentName === SpeechRecognitionUnexpectedIntentName.STOP_INTENT) {
            unrecognizedSpeechCountMetric.count(0)
            return Promise.reject(new StopIntentException({
                message: 'Got stop intent',
            }))
        }

        return Promise.reject(new UnexpectedIntentException({
            message: 'Unexpected intent ' + response.intentName,
        }))
    }

    /**
     * Create config object in accordance with SpeechRecognitionBotConfiguration.
     *
     * @param {string} botName - Example: 'PrimeNow_GetProblemSolve'
     * @param {number} recordTimeInMillis - the length of time that the speech is recorded in milliseconds
     * @param {array} alternativeBots - array of alternativeBots passed by caller. Example: ['PrimeNowGetProblemSolve']
     *  Note: alternativeBots key can only be non-empty for the target bot (lex bot called with top priority)
     *
     * @return {object} config - bot config object
     *  Example:
     *      {
     *          botName: 'Custom_PrimeNow_GetProblemSolveBot',
     *          recordTimeInMillis: 2000,
     *          expectedIntentName: 'Custom_PrimeNow_GetProblemSolveIntent',
     *          expectedSlotName: 'Custom_PrimeNow_GetProblemSolveSlot',
     *          alternativeBots: ['PrimeNow_GetProblemSolve']
     *          postProcessCallback: processDefault
     *      }
     */
    private static generateBotConfig (botName, recordTimeInMillis, alternativeBots) {
        const base = 'Custom_' + botName
        const bot = base + 'Bot'
        const expectedIntentName = base + 'Intent'
        const expectedSlotName = base + 'Slot'

        return {
            botName: bot,
            recordTimeInMillis,
            expectedIntentName,
            expectedSlotName,
            alternativeBots,
            postProcessCallback: SpeechRecognitionService.processDefault,
        }
    }

    /**
     * Creates a Lex config object from the template
     * @param {string} botName - The name of the bot to call.
     * @param {audioBuffer} audioBuffer - The audio buffer to stream to Lex.
     * @return {object} The config object for the request.
     */
    private static generateLexConfig (botName, audioBuffer) {
        const copy = _.cloneDeep(SpeechRecognitionService.lexConfigTemplate)
        return Object.assign(copy, {
            botName,
            inputStream: audioBuffer,
            userId: UUIDUtils.generate(),
        })
    }

    /**
     * Generates a response object that can be used outside.
     * @param {any} data - The data extracted from the bot.
     * @param {string} botName - Name of bot that generated the response
     * @return - Object containing the extracted data.
     *  Example:
     *      {
     *          data: 10,
     *          botName: 'Peppermint_GetNumberBot'
     *      }
     */
    private static generateResponseObject (data, botName) {
        return {
            data,
            botName,
        }
    }

    /**
     * Default processor used to process Lex responses. It takes care of extracting the expected slot from the
     * response.
     *
     * @param {object} config - config with the same structure as SpeechRecognitionBotConfiguration.
     * @param {object} validResponse - The response from Lex (this will assume that the slot exists and will not validate)
     * @return {Promise} A promise that resolves to a {ResponseObject} without modifying Lex's data type.
     */
    private static processDefault (config, validResponse) {
        return Promise.resolve(SpeechRecognitionService.generateResponseObject(validResponse.slots[config.expectedSlotName], config.botName))
    }

    /**
     * Processor for dates. Takes care of extracting and validating the date slot from the response.
     *
     * @param {object} config - config with the same structure as SpeechRecognitionBotConfiguration.
     * @param {object} validResponse - The response from Lex (this will assume that the slot exists and will not validate)
     * @return {Promise} A promise that resolves to a {ResponseObject} with the data type as number.
     */
    private static processDate (config, validResponse) {
        const dateStr = validResponse.slots[config.expectedSlotName]

        if (!SpeechRecognitionService.dateValidationRegex.test(dateStr)) {
            return Promise.reject(new InvalidSpeechInputException({
                message: 'Got invalid date',
            }))
        }

        return Promise.resolve(SpeechRecognitionService.generateResponseObject(dateStr, config.botName))
    }

    /**
     * Processor for numbers (including negative and float numbers). Takes care of extracting the numeric slot
     * from the response and converting it to the appropriate type (int/float).
     * @param {object} config - config with the same structure as SpeechRecognitionBotConfiguration.
     * @param {object} validResponse - The response from Lex (this will assume that the slot exists and will not validate)
     * @return {Promise} A promise that resolves to a {ResponseObject} with the data type as number.
     */
    private static processNumber (config, validResponse) {
        const numberStr = validResponse.slots[config.expectedSlotName]

        if (numberStr.indexOf('.') >= 0) {
            return Promise.resolve(SpeechRecognitionService.generateResponseObject(parseFloat(numberStr), config.botName))
        }

        return Promise.resolve(SpeechRecognitionService.generateResponseObject(parseInt(numberStr, 10), config.botName))
    }

    /**
     * Processor for positive numbers. Takes care of extracting and validating the numeric slot.
     *
     * @param {object} config - config with the same structure as SpeechRecognitionBotConfiguration.
     * @param {object} validResponse - The response from Lex (this will assume that the slot exists and will not validate)
     * @return {Promise} A promise that resolves to a {ResponseObject} with the data type as number guaranteed to be >= 0 and
     *  be an integer.
     */
    private static processPositiveNumber (config, validResponse) {
        const numberStr = validResponse.slots[config.expectedSlotName]

        if (numberStr.indexOf('.') >= 0) {
            return Promise.reject(new InvalidSpeechInputException({
                message: 'Got invalid positive integer (decimal point)',
            }))
        }

        // Negative numbers are currently not supported, but we still add this here just in case
        // https://tiny.amazon.com/19jyud62w/forudeveamazques5218howt
        const numberInt = parseInt(numberStr, 10)
        if (numberInt < 0) {
            return Promise.reject(new InvalidSpeechInputException({
                message: 'Got invalid positive integer (negative number)',
            }))
        }

        return Promise.resolve(SpeechRecognitionService.generateResponseObject(parseInt(numberStr, 10), config.botName))
    }

    /**
     * Asynchronously calls each of the provided lex bots and returns an array of objects that each describes
     * the outcome of each promise.
     * This enables similar functionality to Promise.allSettled():
     *  https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/allSettled
     * @TODO: https://sim.amazon.com/issues/F4-7173
     *
     * @param {array} promiseArray - Array of functions used to call lex bots.
     */
    private static allSettled (promiseArray) {
        console.info('Calling bots...')
        const mappedPromises = promiseArray.map((p) => {
            return p
                .then((data) => {
                    return {
                        status: 'fulfilled',
                        value: data,
                    }
                })
                .catch((error) => {
                    return {
                        status: 'rejected',
                        reason: error,
                    }
                })
        })
        return Promise.all(mappedPromises)
    }
}
